/**
 * §P.3 · La cuota de un alumno.
 *
 * Va en la ficha del alumno y no en un listado propio, y no es por comodidad: el
 * listado de cuotas devuelve **solo identificadores** —`student_id`, `payer_id`,
 * `fee_plan_id`— sin un nombre, al contrario que `enrollments`, que sí trae
 * `first_name` y `group_name`. Aquí el alumno ya se sabe.
 *
 * ## El desglose se enseña entero
 *
 * `GET /subscriptions/{id}/amount` devuelve base, porcentajes, importes fijos y qué
 * descuento aporta cada resta. Y hay que pintarlo todo:
 *
 * > Es lo que permite que el club le explique a una madre por qué paga 26,50 € en
 * > lugar de 29 €, que es la llamada que recibe cada septiembre. Un «26,50 €» a
 * > pelo no se puede defender por teléfono.
 *
 * **Y no se recalcula nada aquí.** Lo calcula una vista de la base de datos. Si la
 * pantalla hiciera su cuenta, el panel y el recibo discreparían en un céntimo el
 * día que caduca un descuento — y de las dos cifras, la familia se cree la del
 * recibo.
 *
 * ## `amount_cents: null` no es 0 €
 *
 * Es una cuota pausada o cancelada: **no cuesta todavía**. Se pinta el estado, no
 * un importe, porque «0 €» en la ficha de un niño que paga 29 € es una llamada.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { etagDe } from '@/shared/api/concurrencia';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { euros, fecha } from '@/shared/ui/formato';
import type { Pagador } from '@/club/pagadores/tipos';
import {
  aCentimos,
  aEuros,
  etiquetaDePeriodo,
  type Cuota,
  type Desglose,
  type PaginaDeCuotas,
  type Tarifa,
} from '@/club/cuotas/tipos';
import { pausaValida, vueltaPropuesta } from '@/club/cuotas/pausas';
import { DescuentosDeLaCuota } from '@/club/descuentos/DescuentosDeLaCuota';
import { aquiMismo, FaltaAntes } from '@/shared/ui/FaltaAntes';

type Haciendo = 'nada' | 'asignar' | 'cambiar' | 'pausar' | 'cancelar';

const hoyComoDia = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

export function CuotaDelAlumno({
  club,
  alumnoId,
  puedeEditar,
  alAbrirPagador,
}: {
  club: string;
  alumnoId: string;
  puedeEditar: boolean;
  alAbrirPagador: (pagadorId: string) => void;
}) {
  const cache = useQueryClient();
  const [haciendo, setHaciendo] = useState<Haciendo>('nada');
  const [problema, setProblema] = useState<Problema | null>(null);
  const [yaTiene, setYaTiene] = useState<Problema | null>(null);

  // El formulario, compartido por asignar y cambiar.
  const [pagadorId, setPagadorId] = useState('');
  const [tarifaId, setTarifaId] = useState('');
  const [pactado, setPactado] = useState('');
  const [pausaDesde, setPausaDesde] = useState(hoyComoDia);
  const [pausaHasta, setPausaHasta] = useState(() => vueltaPropuesta());

  /*
   * La cuota del alumno. Se filtra por `student_id` y **sin filtrar por estado**:
   * hace falta ver también la cancelada, porque «es lo que explica los recibos del
   * curso pasado» y porque sin verla nadie entiende por qué no se le cobra.
   */
  const cuotas = useQuery({
    queryKey: ['cuotas', club, alumnoId],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).subscriptions.get({
        query: { limit: 25, student_id: alumnoId },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PaginaDeCuotas;
    },
  });

  // «Un alumno, una cuota»: la viva es la que no está cancelada.
  const viva = (cuotas.data?.data ?? []).find((c) => c.status !== 'cancelled');
  const canceladas = (cuotas.data?.data ?? []).filter(
    (c) => c.status === 'cancelled'
  );

  const detalle = useQuery({
    queryKey: ['cuota', club, viva?.id],
    enabled: Boolean(viva),
    queryFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .subscriptions({ subscriptionId: viva!.id })
        .get();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return { cuota: r.data as unknown as Cuota, etag: etagDe(r) };
    },
  });

  const desglose = useQuery({
    queryKey: ['desglose', club, viva?.id],
    enabled: Boolean(viva),
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .subscriptions({ subscriptionId: viva!.id })
        .amount.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Desglose;
    },
  });

  const tarifas = useQuery({
    queryKey: ['tarifas', club, false],
    enabled: haciendo === 'asignar' || haciendo === 'cambiar',
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club })['fee-plans'].get({
        query: {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Tarifa[];
    },
  });

  // Su propia clave: la primera página de «Pagadores» es `['pagadores', club, '', null]`
  // y guarda la página entera, no la lista. Compartiéndola, abrir Pagadores después
  // de dar una cuota leía esta lista como página y la pantalla se rompía (16-10-2026).
  // Empieza igual, así que crear un pagador la sigue refrescando.
  const pagadores = useQuery({
    queryKey: ['pagadores', club, 'para-la-cuota'],
    enabled: haciendo === 'asignar' || haciendo === 'cambiar',
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).payers.get({
        query: { limit: 100 },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return (data as unknown as { data: Pagador[] }).data;
    },
  });

  const refrescar = () => {
    void cache.invalidateQueries({ queryKey: ['cuotas', club, alumnoId] });
    void cache.invalidateQueries({ queryKey: ['cuota', club] });
    void cache.invalidateQueries({ queryKey: ['desglose', club] });
  };

  const cerrar = () => {
    setHaciendo('nada');
    setProblema(null);
    setYaTiene(null);
  };

  const asignar = useMutation({
    mutationFn: async () => {
      const importe = pactado.trim() === '' ? null : aCentimos(pactado);
      if (pactado.trim() !== '' && importe === null) {
        throw new ErrorDeApi({
          title: 'El precio pactado no se entiende',
          status: 400,
          detail:
            'Escríbelo en euros: 20 o 20,50. O déjalo vacío para usar la tarifa.',
        });
      }

      const r = await api.v1.clubs({ club }).subscriptions.post({
        student_id: alumnoId,
        payer_id: pagadorId,
        fee_plan_id: tarifaId,
        ...(importe !== null ? { custom_amount_cents: importe } : {}),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      cerrar();
      refrescar();
    },
    onError: (e) => {
      const p = aProblema(e);
      // «Un alumno, una cuota.» No se ofrece «crear otra»: se lleva a editar la
      // que ya tiene, porque dos cuotas vivas son dos líneas en el mismo recibo
      // todos los meses.
      if (p.code === 'student_already_subscribed') setYaTiene(p);
      else setProblema(p);
    },
  });

  const cambiar = useMutation({
    mutationFn: async (cuerpo: Record<string, unknown>) => {
      const r = await api.v1
        .clubs({ club })
        .subscriptions({ subscriptionId: viva!.id })
        .patch(cuerpo, {
          headers: detalle.data?.etag ? { 'if-match': detalle.data.etag } : {},
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      cerrar();
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (cuotas.isPending)
    return <Cargando filas={2} etiqueta="Cargando la cuota" />;

  if (cuotas.isError)
    return (
      <PanelError
        problema={aProblema(cuotas.error)}
        reintentar={() => void cuotas.refetch()}
      />
    );

  // ── Asignar o cambiar ────────────────────────────────────────────────────
  if (haciendo === 'asignar' || haciendo === 'cambiar') {
    const esCambio = haciendo === 'cambiar';

    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          if (esCambio) {
            const importe = pactado.trim() === '' ? null : aCentimos(pactado);
            cambiar.mutate({
              fee_plan_id: tarifaId,
              payer_id: pagadorId,
              custom_amount_cents: importe,
            });
          } else {
            asignar.mutate();
          }
        }}
      >
        <h3>{esCambio ? 'Cambiar la cuota' : 'Dar cuota a este alumno'}</h3>

        {yaTiene && (
          <div className="aviso" role="status">
            <strong>{yaTiene.title}</strong> {yaTiene.detail}
            <div className="hueco-acciones">
              <button
                type="button"
                onClick={() => {
                  setYaTiene(null);
                  setHaciendo('nada');
                  refrescar();
                }}
              >
                Ver la que ya tiene
              </button>
            </div>
          </div>
        )}

        <div className="rejilla">
          <label>
            Tarifa
            <select
              required
              value={tarifaId}
              onChange={(e) => setTarifaId(e.target.value)}
              disabled={!tarifas.data}
            >
              <option value="">Elige una tarifa</option>
              {(tarifas.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} · {euros(t.amount_cents)} ·{' '}
                  {etiquetaDePeriodo(t.period).toLowerCase()}
                </option>
              ))}
            </select>
          </label>

          <label>
            Quién paga
            <select
              required
              value={pagadorId}
              onChange={(e) => setPagadorId(e.target.value)}
              disabled={!pagadores.data}
            >
              <option value="">Elige un pagador</option>
              {(pagadores.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.legal_name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Precio pactado
            <input
              inputMode="decimal"
              placeholder="Vacío: el de la tarifa"
              value={pactado}
              onChange={(e) => setPactado(e.target.value)}
            />
          </label>
        </div>

        {tarifas.data?.length === 0 && (
          <FaltaAntes
            accion="Crear la primera tarifa"
            destino="/cobros/tarifas"
            nuevo
            volver={aquiMismo()}
          >
            <strong>Tu club todavía no tiene tarifas</strong>, y una cuota es
            una tarifa asignada a un alumno. Créala y volverás a esta ficha.
          </FaltaAntes>
        )}

        {/* El precio pactado no anula los descuentos: se aplican encima. */}
        {pactado.trim() !== '' && (
          <div className="nota">
            <strong>Un precio pactado sustituye al de la tarifa</strong>, pero{' '}
            <strong>los descuentos se siguen aplicando encima</strong>. Si la
            idea era un precio final, revisa que no tenga descuentos.
          </div>
        )}

        {(pagadores.data?.length ?? 1) === 0 && (
          <div className="nota">
            <strong>Este club no tiene pagadores todavía.</strong> Una cuota
            necesita saber quién paga: créalo desde la ficha de un tutor, con
            «Hacer pagador», y así el recibo se ve en la app de familias.
          </div>
        )}

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button
            type="submit"
            disabled={asignar.isPending || cambiar.isPending}
          >
            {asignar.isPending || cambiar.isPending
              ? 'Guardando…'
              : esCambio
                ? 'Guardar la cuota'
                : 'Dar la cuota'}
          </button>
          <button type="button" className="btn-plano" onClick={cerrar}>
            Cancelar
          </button>
        </div>

        {esCambio && (
          <p className="tenue">
            Afecta al <strong>próximo</strong> recibo. Los ya emitidos guardan
            su importe.
          </p>
        )}
      </form>
    );
  }

  // ── Pausar ───────────────────────────────────────────────────────────────
  if (haciendo === 'pausar') {
    const dias = pausaValida(pausaDesde, pausaHasta);

    return (
      <form
        className="hueco hueco-confirmar"
        onSubmit={(e) => {
          e.preventDefault();
          cambiar.mutate({
            status: 'paused',
            paused_from: pausaDesde,
            paused_to: pausaHasta,
          });
        }}
      >
        <h3>Pausar la cuota</h3>
        <p>
          Mientras esté pausada no se le cobra. La cuota sigue existiendo y
          vuelve sola.
        </p>

        <div className="rejilla">
          <label>
            Desde
            <input
              type="date"
              required
              value={pausaDesde}
              onChange={(e) => setPausaDesde(e.target.value)}
            />
          </label>
          <label>
            Hasta
            <input
              type="date"
              required
              value={pausaHasta}
              onChange={(e) => setPausaHasta(e.target.value)}
            />
          </label>
        </div>

        {/*
          La fecha de vuelta es obligatoria y viene propuesta. El contrato dice por
          qué, y merece decírselo al club: una pausa sin vuelta se olvida.
        */}
        <p className="tenue">
          La fecha de vuelta <strong>no es opcional</strong>: una pausa sin
          fecha es una cuota que se deja de cobrar en julio y de la que nadie se
          acuerda en octubre. Se puede prorrogar cambiándola.
        </p>

        {!dias && (
          <div className="aviso">
            <strong>La vuelta tiene que ser posterior al inicio.</strong>
          </div>
        )}

        {problema && <PanelError problema={problema} />}

        <div className="hueco-acciones">
          <button type="submit" disabled={cambiar.isPending || !dias}>
            {cambiar.isPending ? 'Pausando…' : 'Pausar la cuota'}
          </button>
          <button type="button" className="btn-plano" onClick={cerrar}>
            Dejarlo
          </button>
        </div>
      </form>
    );
  }

  // ── Cancelar ─────────────────────────────────────────────────────────────
  if (haciendo === 'cancelar') {
    return (
      <div className="hueco hueco-confirmar">
        <h3>¿Cancelar la cuota?</h3>
        <p>Se deja de cobrar a este alumno a partir de ahora.</p>
        <ul className="lista-clara">
          <li>
            <strong>No se borra.</strong> La cuota cancelada sigue aquí y es lo
            que explica los recibos del curso pasado.
          </li>
          <li>
            <strong>Si es temporal, pausa en vez de cancelar.</strong> Una pausa
            vuelve sola en la fecha que digas; una cancelación hay que
            rehacerla.
          </li>
        </ul>

        {problema && <PanelError problema={problema} />}

        <div className="hueco-acciones">
          <button
            type="button"
            className="btn-peligro"
            disabled={cambiar.isPending}
            onClick={() => cambiar.mutate({ status: 'cancelled' })}
          >
            {cambiar.isPending ? 'Cancelando…' : 'Sí, cancelar la cuota'}
          </button>
          <button type="button" className="btn-plano" onClick={cerrar}>
            Dejarlo
          </button>
        </div>
      </div>
    );
  }

  // ── Sin cuota ────────────────────────────────────────────────────────────
  if (!viva) {
    return (
      <>
        <Vacio
          titulo="Este alumno no tiene cuota"
          explicacion="Sin cuota no entra en la remesa, así que no se le emite recibo. Hace falta una tarifa y saber quién paga."
          {...(puedeEditar
            ? {
                accion: (
                  <button
                    type="button"
                    onClick={() => {
                      setTarifaId('');
                      setPagadorId('');
                      setPactado('');
                      setHaciendo('asignar');
                    }}
                  >
                    Dar cuota
                  </button>
                ),
              }
            : {})}
        />
        {canceladas.length > 0 && (
          <p className="tenue">
            Tuvo {canceladas.length}{' '}
            {canceladas.length === 1 ? 'cuota cancelada' : 'cuotas canceladas'},
            la última desde {fecha(canceladas[0]!.starts_on)}. Se quedan para
            explicar los recibos de entonces.
          </p>
        )}
      </>
    );
  }

  // ── La cuota, con su desglose ────────────────────────────────────────────
  return (
    <>
      {desglose.isPending && (
        <Cargando filas={2} etiqueta="Calculando la cuota" />
      )}

      {desglose.isError && (
        <PanelError
          problema={aProblema(desglose.error)}
          reintentar={() => void desglose.refetch()}
        />
      )}

      {desglose.data && <Desglosado d={desglose.data} cuota={viva} />}

      {/*
        Los descuentos, debajo del desglose porque son su **por qué**. Y se
        invalida el desglose al tocarlos: el total lo calcula el servidor.
      */}
      <DescuentosDeLaCuota
        club={club}
        cuotaId={viva.id}
        puedeEditar={puedeEditar}
      />

      <div className="acciones">
        {puedeEditar && (
          <>
            <button
              type="button"
              className="btn-plano"
              onClick={() => {
                setTarifaId(viva.fee_plan_id);
                setPagadorId(viva.payer_id);
                setPactado(
                  viva.custom_amount_cents === null
                    ? ''
                    : aEuros(viva.custom_amount_cents)
                );
                setHaciendo('cambiar');
              }}
            >
              Cambiar tarifa o pagador
            </button>

            {viva.status === 'active' ? (
              <button
                type="button"
                className="btn-plano"
                onClick={() => {
                  setPausaDesde(hoyComoDia());
                  setPausaHasta(vueltaPropuesta());
                  setHaciendo('pausar');
                }}
              >
                Pausar
              </button>
            ) : (
              <button
                type="button"
                className="btn-plano"
                onClick={() => cambiar.mutate({ status: 'active' })}
                disabled={cambiar.isPending}
              >
                Reanudar
              </button>
            )}

            <button
              type="button"
              className="btn-plano"
              onClick={() => setHaciendo('cancelar')}
            >
              Cancelar la cuota
            </button>
          </>
        )}

        <button
          type="button"
          className="btn-plano"
          onClick={() => alAbrirPagador(viva.payer_id)}
        >
          Ver quién paga
        </button>
      </div>

      {problema && <PanelError problema={problema} />}
    </>
  );
}

/**
 * El desglose, entero.
 *
 * Y con el estado por delante del importe cuando no hay importe: `amount_cents:
 * null` **no es 0 €**, es una cuota pausada. Pintar «0 €» en la ficha de un niño
 * que paga 29 € es una llamada al club.
 */
function Desglosado({ d, cuota }: { d: Desglose; cuota: Cuota }) {
  const pausada = cuota.status === 'paused';
  const sinImporte = d.amount_cents === null || d.amount_cents === undefined;

  return (
    <div className="desglose">
      <div className="desglose-total">
        {sinImporte ? (
          <>
            <strong className="desglose-estado">
              {pausada ? 'Pausada' : 'Sin importe'}
            </strong>
            <span className="tenue">
              {pausada && cuota.paused_to
                ? `Vuelve el ${fecha(cuota.paused_to)}. No se le cobra hasta entonces.`
                : 'No se le está cobrando.'}
            </span>
          </>
        ) : (
          <>
            <strong>{euros(d.amount_cents)}</strong>
            <span className="tenue">
              {d.fee_plan ?? 'Sin tarifa'}
              {pausada ? ' · pausada' : ''}
            </span>
          </>
        )}
      </div>

      {/* El por qué, que es lo que se defiende por teléfono. */}
      {!sinImporte && (
        <ul className="desglose-lineas">
          <li>
            <span>
              {d.agreed_price ? 'Precio pactado' : `Tarifa ${d.fee_plan ?? ''}`}
            </span>
            <span className="num">{euros(d.base_cents)}</span>
          </li>

          {d.percent_off !== null && d.percent_off > 0 && (
            <li className="desglose-resta">
              <span>Descuentos en porcentaje</span>
              <span className="num">−{d.percent_off} %</span>
            </li>
          )}

          {d.fixed_off_cents !== null && d.fixed_off_cents > 0 && (
            <li className="desglose-resta">
              <span>Descuentos de importe fijo</span>
              <span className="num">−{euros(d.fixed_off_cents)}</span>
            </li>
          )}

          <li className="desglose-suma">
            <span>Paga</span>
            <span className="num">{euros(d.amount_cents)}</span>
          </li>
        </ul>
      )}

      {d.discount_codes && d.discount_codes.length > 0 && (
        <p className="tenue">
          Descuentos aplicados: {d.discount_codes.join(', ')}
        </p>
      )}

      {d.agreed_price && (
        <p className="tenue">
          Es un <strong>precio pactado</strong>, no el de la tarifa. Los
          descuentos se aplican encima.
        </p>
      )}
    </div>
  );
}
